import Handlebars from '@sparta.global.vendors/handlebars';

/**
 * @const STRINGS
 * @description constant for storing message strings
 */
const STRINGS = {
  NO_card_JCR_ERROR: 'No card JCR objects provided to helper-card.',
  NO_card_CONTENT_ERROR: '<strong>Error:</strong> No card content available.',
  DEFAULT_ADA_ERROR_TEXT: ', contains an error message',
  DEFAULT_ADA_WARNING_TEXT: ', contains an warning message',
  DEFAULT_ADA_SUCCESS_TEXT: ', contains a success message'
};
const allowedTitleTags = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'div'];
const DEFAULT_BASE_CLASS = 'spa-card spa-card--overlay';

/**
 * Loops through the JCR contexts and matches the context for the current card content, then
 * compiles and returns the content into a Handlebars template string.
 * @param {object} card - Object of JCR parameters for the current card.
 * @param {object} contexts - JCR contexts provided by the Handlebars template.
 * @param {function} template - Handlebars template used to compile the card content.
 * @returns {string} Compiled Handlebars template string with dynamic card content.
 */
function getDynamicContent(card, contexts, template) {
  let cardContent;
  let cardContext;

  // console.log('card - getDynamicContent', {card, contexts, template})

  // match content to JCR
  contexts.forEach(context => {
    if (card.content === context.jcr) {
      cardContext = context;
    }
  });

  // match content to JCR
  if (contexts.length > 0) {
    cardContext = contexts[0];
  } else {
    cardContext = contexts;
  }

  // check to make sure we have a file string to look up
  if (contexts && contexts.length > 0) {
    // if content comes from partial
    if (card.dynamicContent === 'partial') {
      cardContent = template(cardContext);
    } else if (card.dynamicContent === 'markdown') {
      // if content comes from Markdown
      cardContent = template({
        content: cardContext
      });
    } else {
      // else just use string as given to content
      cardContent = card.content;
    }
  } else {
    // if no context given print error
    cardContent = card.content || 'No card content found';
  }

  // return handlebar template
  return cardContent;
}

/**
 * @method cardHelper
 * @param {object} jcr - JSON object used to build the card.
 * @param {object} args - JSON options object used to build the card.
 * @returns {Node} `Handlebars.SafeString` version of `spa-card__container` element.
 * @description This pieces together all the HTML elements needed to make an squeezebox.
 */
function cardHelper(partials, jcr, ...args) {
  if (!jcr) {
    throw new Error(STRINGS.NO_card_JCR_ERROR);
  }

  // Combine all the additional arguments into an array of contexts for the partials/Markdown.
  const jcrContexts = Array.prototype.slice.call(args, 0, -1);

  // Create settings object from JCR information.
  // ----------------------------------- //
  const options = jcr;
  const settings = new function SettingsContructor() {
    // Base.
    this.getID = options.id;
    if (options.size === 'xsmall' || options.size === 'small' || options.size === 'large') {
      this.size = `spa-card--${options.size}`;
    } else {
      this.size = 'spa-card--medium';
    }

    // Card attributes.
    this.id = this.getID ? `id='${this.getID.toLowerCase()}'` : '';
    this.caret = options.caret ? `card-caret-${options.caret}` : '';
    this.additionalClass = `${this.caret} ${options.class || ''}`;
    this.class = `${DEFAULT_BASE_CLASS} ${this.size} ${this.additionalClass}`;

    // Jcr data.
    this.title = options.title ? options.title : null;
    this.titleTag = options.titleTag && allowedTitleTags.indexOf(options.titleTag) >= 0 ? options.titleTag : 'div';

    // ADA controls.
    this.dismissible = !!options.dismissible;
    this.dismissibleADA = options.dismissibleADA;
    this.dismissibleADA = this.dismissibleADA === false ? 'Close Card' : this.dismissibleADA;
    this.dismissibleDescribedBy = options.dismissibleDescribedBy || '';
    this.dismissibleClass = options.dismissibleClass || '';
    this.disableDismissibleADA = options.disableDismissibleADA && options.disableDismissibleADA.toString() === 'true' ? true : false;

    // If no dismissible, add an atomic class
    if (!this.dismissible) {
      this.class = `${this.class} spa-card__dismissible--none`;
    }
    this.layerStartADA = options.layerStartADA ? options.layerStartADA : false;
    this.layerStartADAShowFocus = options.layerStartADAShowFocus ? 'show-on-focus' : '';
    this.layerEndADA = options.layerEndADA ? options.layerEndADA : false;
    this.layerEndADAShowFocus = options.layerEndADAShowFocus ? 'show-on-focus' : '';

    // Base config for custom attribute adding (used by modals).
    this.config = options.config;
    this.titleID = this.config && this.config.titleID ? this.config.titleID : '';
    this.bodyID = this.config && this.config.bodyID ? this.config.bodyID : '';

    // App-specific data.
    this.card = options.markup;
    this.cardContent = options.markup && options.markup.content ? jcr.markup.content : null;
    this.dynamicContent = options.markup && options.markup.dynamicContent ? options.markup.dynamicContent : null;
    this.partialName = options.markup && options.markup.partialName ? options.markup.partialName : null;
    this.templateString = this.dynamicContent;

    // Get card head data.
    this.header = options.head || '';
    this.headerClass = this.header.class || '';
    this.headerTitleClass = this.header.titleClass || '';
    this.headerSkin = options.skin ? `spa-card__head-wrapper-${options.skin}` : '';
    this.headerIcon = options.skin === 'red' || options.skin === 'blue' ? 'spa-card__dismissible-white' : '';

    // Get card footer data.
    this.footer = options.foot || '';
    this.footerConfig = this.footer.config || {};
    this.footerData = this.footer.data || [];

    // Card footer layout options.
    this.footerLayoutClass = this.footerConfig.layout || '';
    this.footerLayoutDataAttr = this.footerConfig.layout ? `data-layout="${this.footerConfig.layout}"` : '';
    this.footerStackedDesktop = this.footerConfig.stackOnDesktop ? `data-stacked-desktop="${this.footerConfig.stackOnDesktop}"` : '';
    this.footerStackedTablet = this.footerConfig.stackOnTablet ? `data-stacked-tablet="${this.footerConfig.stackOnTablet}"` : '';
    this.footerStackedMobile = this.footerConfig.stackOnMobile === false ? `data-stacked-mobile="${this.footerConfig.stackOnMobile}"` : 'data-stacked-mobile="true"';

    // Helper partials (empty to start out with).
    this.partials = new function PartialsContructor() {
      // Base.
      this.cardWrapper = '';
      this.cards = '';

      // Parts.
      this.cardDismissible = '';
      this.cardHead = '';
      this.cardBody = '';
      this.cardFoot = '';
    }();
  }();

  // check for card content
  let innerContent = '';
  if (settings.cardContent || settings.cardContent != null) {
    // const setTemplate = () => {
    //   const type = {
    //     partial: `{{> ${settings.partialName} }}`,
    //     markdown: '{{{content.markdown}}}',
    //     default: settings.cardContent,
    //   };
    //   return (type[settings.dynamicContent] || type.default);
    // };

    //  const partialTemplate = partials[settings.partialName] ? partials[settings.partialName] :  () => `PARTIAL [${settings.partialName}] NOT FOUND`
    const partialTemplate = partials[settings.partialName] ? partials[settings.partialName] : Handlebars.compile('{{{content.markdown}}}');

    //  const generatedMarkdown = markdownTemplate({})

    // console.log('generatedMarkdown', generatedMarkdown)

    // const templateString = setTemplate();
    // const partialTemplate = Handlebars.compile(templateString);
    innerContent = getDynamicContent(settings.card, jcrContexts, partialTemplate);
    // innerContent = partialTemplate({})
    // innerContent = 'UNDER CONSTRUCTION'
  }

  // Loop through footer data array where each object is a link/text/button
  let footerContent = '';
  settings.footerData.forEach(item => {
    // get element type from JCR
    const elementType = item.elementType ? item.elementType : 'a';

    // if element type is button, add some atomics to proper style it
    const elementTypeClass = elementType === 'button' ? 'spa-btn spa-btn--link' : '';

    // if button, add type
    const typeAttr = elementType === 'button' ? 'type="button"' : '';

    // build attributes
    const className = item.class ? `class="spa-card__foot-link ${elementTypeClass} ${item.class}"` : `class="spa-card__foot-link ${elementTypeClass}"`;
    const id = item.id ? `id="${item.id}"` : '';
    const URL = item.URL ? `href="${item.URL}"` : '';
    const label = item.ariaLabel ? `aria-label="${item.ariaLabel}"` : '';

    // build footer items
    if (item.content) {
      footerContent += `
        <${elementType} ${typeAttr} ${className} ${id} ${URL} ${label}>
          ${item.content}
        </${elementType}>
      `;
    }
  });

  // Create Parts
  // ----------------------------------- //
  if (settings.dismissible) {
    settings.partials.cardDismissible += `
      <button
        class="spa-card__dismissible spa-icon icon-close ${settings.headerIcon} ${settings.dismissibleClass}"
        ${settings.dismissibleDescribedBy ? `aria-describedby="${settings.dismissibleDescribedBy}"` : settings.dismissibleADA ? `aria-label="${settings.dismissibleADA}"` : ''}
        type="button"
      ></button>
    `;
  }
  settings.partials.cardHead += `
    <div class="spa-card__head ${settings.headerClass}">
      ${settings.dismissible === false && settings.dismissibleADA && settings.dismissibleADA !== false && typeof settings.dismissibleADA === 'string' ? `
          <div class="spa-card__ada-msg ada-hidden" tabindex="-1">${settings.dismissibleADA}</div>
        ` : ''}
      <div class="spa-card__head-wrapper ${settings.headerSkin}">
        ${settings.partials.cardDismissible}
        ${settings.title ? `
            <${settings.titleTag} ${settings.titleID ? `id="${settings.titleID}"` : ''} class="spa-card__title ${settings.headerTitleClass}">
              ${settings.title}
            </${settings.titleTag}>
          ` : ''}
      </div>
    </div>
  `;

  // if no title and dismissible just product the extra ADA tag
  if (!settings.dismissible && !settings.title) {
    settings.partials.cardHead = `<div class="spa-card__ada-msg ada-hidden" tabindex="-1">${settings.dismissibleADA}</div>`;
  }

  // Remove card head
  if (settings.dismissible === false && settings.disableDismissibleADA === true) {
    settings.partials.cardHead = '';
  }
  settings.partials.cardBody += `
    <div ${settings.bodyID ? `id="${settings.bodyID}"` : ''} class="spa-card__body">
      ${innerContent}
    </div>
  `;
  settings.partials.cardFoot += footerContent ? `
      <div
        class="spa-card__foot spa-card__foot-${settings.footerLayoutClass}"
        ${settings.footerLayoutDataAttr}
        ${settings.footerStackedDesktop}
        ${settings.footerStackedTablet}
        ${settings.footerStackedMobile}
      >
        ${footerContent}
      </div>
    ` : '';

  // Create card
  // ----------------------------------- //
  settings.partials.cards += `
    <div ${settings.id} class="${settings.class}">
      ${settings.layerStartADA ? `
          <div
            class="
              spa-card__ada-msg
              spa-card__ada-msg-layer-start
              ${settings.layerStartADAShowFocus}
              ada-hidden
            "
            tabindex="-1"
          >${settings.layerStartADA}</div>
        ` : ''}
      ${settings.partials.cardHead}
      ${settings.partials.cardBody}
      ${settings.partials.cardFoot}
      ${settings.layerEndADA ? `
          <div
            class="
              spa-card__ada-msg
              spa-card__ada-msg-layer-end
              ${settings.layerEndADAShowFocus}
              ada-hidden
            "
            tabindex="-1"
          >${settings.layerEndADA}</div>
        ` : ''}
    </div>
  `;

  // LAST STEP - compile everything together
  // ----------------------------------- //
  settings.partials.cardWrapper += `
    ${settings.partials.cards}
  `;

  // return HTML to page
  // ----------------------------------- //
  return new Handlebars.SafeString(settings.partials.cardWrapper);
}
cardHelper.functionName = 'card';

export { cardHelper };
